import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import {
  ArrowLeft, TicketCheck, AlertTriangle, Clock, CheckCircle, UserCheck,
  Send, Loader2, Filter, MessageCircle, Search, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

const STATUS_COLORS = {
  open: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  in_progress: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  waiting_on_user: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  resolved: 'bg-green-500/10 text-green-600 border-green-500/20',
  closed: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
};
const PRIO_COLORS = { low: 'text-gray-500', normal: 'text-blue-400', high: 'text-amber-500', urgent: 'text-red-500' };

export default function HelpDeskAdmin() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (catFilter) params.append('category', catFilter);
      const [ticketsRes, statsRes] = await Promise.all([
        api.get(`/helpdesk/admin/tickets?${params}`),
        api.get('/helpdesk/admin/stats')
      ]);
      setTickets(ticketsRes.data);
      setStats(statsRes.data);
    } catch { toast.error('Failed to load tickets'); }
    finally { setLoading(false); }
  }, [statusFilter, catFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchDetail = async (id) => {
    try {
      const res = await api.get(`/helpdesk/tickets/${id}`);
      setDetail(res.data);
      setSelected(id);
      // Iter-227c — auto-scroll the detail panel into view (small screens stack
      // vertically so a tap would otherwise load the detail below the fold).
      setTimeout(() => {
        const el = document.getElementById('helpdesk-detail-panel');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    } catch { toast.error('Failed to load ticket'); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/helpdesk/tickets/${selected}/respond`, { message: reply, is_internal: isInternal });
      setReply(''); setIsInternal(false);
      fetchDetail(selected);
      toast.success(isInternal ? 'Internal note added' : 'Reply sent');
    } catch { toast.error('Failed'); }
    finally { setSending(false); }
  };

  const updateStatus = async () => {
    if (!newStatus) return;
    try {
      await api.patch(`/helpdesk/admin/tickets/${selected}/status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      setShowStatusDialog(false);
      fetchDetail(selected);
      fetchAll();
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="helpdesk-admin">
      <header className="bg-gray-50 border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/home')} className="text-gray-500 hover:text-gray-900 hover:bg-gray-100"><ArrowLeft className="w-5 h-5" /></Button>
            <TicketCheck className="w-5 h-5 text-[#03DFFC]" />
            <h1 className="text-lg font-semibold text-gray-900">Help Desk</h1>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchAll} className="text-gray-500 hover:text-gray-900" data-testid="refresh-tickets">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            {[
              { label: 'Open', count: stats.open, color: 'text-blue-400', icon: Clock },
              { label: 'In Progress', count: stats.in_progress, color: 'text-amber-500', icon: Loader2 },
              { label: 'Waiting', count: stats.waiting_on_user, color: 'text-purple-400', icon: MessageCircle },
              { label: 'Resolved', count: stats.resolved, color: 'text-green-600', icon: CheckCircle },
              { label: 'Urgent Open', count: stats.urgent_open, color: 'text-red-500', icon: AlertTriangle },
            ].map(s => (
              <Card key={s.label} className="bg-gray-50 border-gray-200">
                <CardContent className="p-3 flex items-center gap-3">
                  <s.icon className={`w-5 h-5 ${s.color}`} />
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{s.count}</p>
                    <p className="text-xs text-gray-500">{s.label}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Ticket List — hidden on mobile when a ticket is selected so the
              detail view takes the full width (Iter-227j mobile fix). */}
          <div className={`lg:col-span-1 space-y-3 ${detail ? 'hidden lg:block' : ''}`}>
            <div className="flex gap-2 flex-wrap">
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="bg-gray-50 border border-gray-200 text-gray-600 text-xs rounded-lg px-2 py-1.5" data-testid="status-filter">
                <option value="">All Status</option>
                {['open', 'in_progress', 'waiting_on_user', 'resolved', 'closed'].map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
              <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="bg-gray-50 border border-gray-200 text-gray-600 text-xs rounded-lg px-2 py-1.5" data-testid="category-filter">
                <option value="">All Categories</option>
                {['staffing', 'coordinators', 'finance', 'accounting', 'technical_support', 'general'].map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
              </select>
            </div>

            {loading ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#03DFFC]" /></div> :
            tickets.length === 0 ? <p className="text-gray-500 text-sm text-center py-8">No tickets found</p> :
            <div className="space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto">
              {tickets.map(t => (
                <Card key={t.id} onClick={() => fetchDetail(t.id)}
                  className={`bg-gray-50 border cursor-pointer transition-all hover:bg-gray-750 ${selected === t.id ? 'border-[#03DFFC]' : 'border-gray-200'}`}
                  data-testid={`admin-ticket-${t.id}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-gray-500">#{t.ticket_number}</span>
                      <Badge className={`text-[10px] ${STATUS_COLORS[t.status]}`}>{t.status.replace(/_/g, ' ')}</Badge>
                      <span className={`text-xs font-medium uppercase ${PRIO_COLORS[t.priority]}`}>{t.priority}</span>
                    </div>
                    <p className="text-sm text-gray-900 truncate">{t.subject}</p>
                    <p className="text-xs text-gray-500 mt-1">{t.user_name} &middot; {t.category.replace(/_/g, ' ')}</p>
                  </CardContent>
                </Card>
              ))}
            </div>}
          </div>

          {/* Ticket Detail */}
          <div className="lg:col-span-2" id="helpdesk-detail-panel">
            {detail && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setDetail(null); setSelected(null); }}
                className="mb-2 lg:hidden text-gray-600"
                data-testid="back-to-tickets-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back to tickets
              </Button>
            )}
            {!detail ? (
              <div className="flex items-center justify-center h-64 text-gray-500">
                <div className="text-center">
                  <TicketCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Select a ticket to view</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4" data-testid="admin-ticket-detail">
                <Card className="bg-gray-50 border-gray-200">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-gray-500">#{detail.ticket_number}</span>
                          <Badge className={`text-xs ${STATUS_COLORS[detail.status]}`}>{detail.status.replace(/_/g, ' ')}</Badge>
                          <span className={`text-xs font-medium uppercase ${PRIO_COLORS[detail.priority]}`}>{detail.priority}</span>
                        </div>
                        <CardTitle className="text-gray-900 text-base">{detail.subject}</CardTitle>
                      </div>
                      <Button size="sm" variant="outline" className="border-gray-600 text-gray-600 hover:bg-gray-100" onClick={() => { setNewStatus(detail.status); setShowStatusDialog(true); }} data-testid="change-status-btn">
                        Status
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{detail.category.replace(/_/g, ' ')} &middot; {new Date(detail.created_at).toLocaleString()}</p>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-sm whitespace-pre-wrap">{detail.description}</p>
                    {detail.attachment_urls?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-200">
                        {detail.attachment_urls.map((url, i) => (
                          <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded hover:bg-[#03DFFC]/10 hover:text-[#03DFFC] transition-colors">
                            Attachment {i + 1}
                          </a>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Responses */}
                <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                  {(detail.responses || []).map(r => (
                    <Card key={r.id} className={`border ${r.is_internal ? 'bg-amber-900/10 border-amber-500/20' : r.responder_role === 'admin' || r.responder_role === 'super_admin' ? 'bg-cyan-900/10 border-[#03DFFC]/20' : 'bg-gray-50/60 border-gray-200'}`}>
                      <CardContent className="p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-gray-900">{r.responder_name}</span>
                          {r.is_internal && <Badge className="bg-amber-500/20 text-amber-500 text-[10px]">Internal</Badge>}
                          {(r.responder_role === 'admin' || r.responder_role === 'super_admin') && !r.is_internal && <Badge className="bg-[#03DFFC]/20 text-[#03DFFC] text-[10px]">Admin</Badge>}
                          <span className="text-[10px] text-gray-500">{new Date(r.created_at).toLocaleString()}</span>
                        </div>
                        <p className="text-sm text-gray-600">{r.message}</p>
                        {r.attachment_urls?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {r.attachment_urls.map((url, i) => (
                              <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 bg-gray-100/50 text-gray-500 text-xs px-2 py-0.5 rounded hover:text-[#03DFFC] transition-colors">
                                File {i + 1}
                              </a>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Reply */}
                <Card className="bg-gray-50 border-gray-200">
                  <CardContent className="p-3">
                    <div className="flex gap-2 mb-2">
                      <Input value={reply} onChange={e => setReply(e.target.value)} placeholder="Type reply..." className="bg-white border-gray-200 text-gray-900 placeholder:text-gray-600 flex-1" data-testid="admin-reply-input" />
                      <Button onClick={sendReply} disabled={sending || !reply.trim()} className="bg-[#03DFFC] hover:bg-cyan-700 text-gray-900" data-testid="admin-send-reply">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                      <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} className="rounded border-gray-600" data-testid="internal-note-check" />
                      Internal note (hidden from user)
                    </label>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status Update Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="sm:max-w-sm" data-testid="status-dialog">
          <DialogHeader>
          <DialogTitle>Update Status</DialogTitle>
          <DialogDescription>Select a new status for this ticket.</DialogDescription>
        </DialogHeader>
          <div className="space-y-2">
            {['open', 'in_progress', 'waiting_on_user', 'resolved', 'closed'].map(s => (
              <button key={s} onClick={() => setNewStatus(s)} data-testid={`set-status-${s}`}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm capitalize transition-all ${newStatus === s ? 'bg-[#03DFFC]/10 text-cyan-600 border border-[#03DFFC]/30' : 'hover:bg-gray-100 text-gray-600'}`}
              >{s.replace(/_/g, ' ')}</button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowStatusDialog(false)}>Cancel</Button>
            <Button onClick={updateStatus} className="bg-[#03DFFC] hover:bg-[#03DFFC] text-gray-900" data-testid="confirm-status-btn">Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
